import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import PublicNav from '../components/PublicNav';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const BLUE = '#2563eb';
const BLUE_DARKER = '#1e40af';
const BLUE_LIGHT = '#f0f9ff';
const BLUE_LIGHT_2 = '#e0f2fe';
const SLATE = '#475569';
const WHITE = '#ffffff';
const OFF_WHITE = '#f8fafc';
const TEXT = '#0a0a0a';
const TEXT_MUTED = '#555555';
const TEXT_FAINT = '#888888';
const BORDER = '#e2e8f0';
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function FadeIn({ children, delay = 0, isMobile, style, axis }) {
  const offset = isMobile ? 12 : 20;
  const duration = isMobile ? 0.4 : 0.6;
  const initial = axis === 'x' ? { opacity: 0, x: -offset * 1.5 } : { opacity: 0, y: offset };
  const whileInView = axis === 'x' ? { opacity: 1, x: 0 } : { opacity: 1, y: 0 };
  return (
    <motion.div initial={initial} whileInView={whileInView} viewport={{ once: true, amount: 0.3 }} transition={{ duration, ease: 'easeOut', delay }} style={style}>
      {children}
    </motion.div>
  );
}

function Eyebrow({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: BLUE, marginBottom: 14 }}>
      {children}
    </div>
  );
}

function HoverBtn({ to, children, big, full }) {
  const [hover, setHover] = useState(false);
  return (
    <Link
      to={to}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: hover ? '#f8fafc' : WHITE,
        color: BLUE_DARKER,
        border: '1px solid ' + WHITE,
        fontWeight: 700,
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 10,
        padding: big ? '16px 36px' : '13px 26px',
        fontSize: big ? 16 : 14,
        width: full ? '100%' : 'auto',
        boxSizing: 'border-box',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
        transform: hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover ? '0 10px 24px rgba(0,0,0,0.2)' : '0 4px 10px rgba(0,0,0,0.12)',
      }}
    >
      {children}
    </Link>
  );
}

const MD_DO = [
  { metric: 'Total Applicants', md: '44,711', do: '24,102' },
  { metric: 'Matriculants', md: '22,712', do: '7,938' },
  { metric: 'Acceptance Rate', md: '44.5%', do: '63%' },
  { metric: 'Avg MCAT', md: '512', do: '504' },
  { metric: 'Avg GPA', md: '3.81', do: '3.63' },
  { metric: 'Avg Age', md: '24', do: '25' },
];

const DEMOGRAPHICS = [
  { group: 'Asian', pct: '31.6%' },
  { group: 'White', pct: '30.2%' },
  { group: 'Hispanic / Latino', pct: '11.5%' },
  { group: 'Black / African American', pct: '8.4%' },
  { group: 'Multiracial', pct: '4.9%' },
  { group: 'Unknown / Other', pct: '8.2%' },
  { group: 'Native / Pacific Islander', pct: '0.4%' },
  { group: 'International', pct: '4.8%' },
];

const MATTERS = [
  { heading: 'MCAT is a floor, not a ceiling.', body: 'Most MD programs have a de facto MCAT cutoff around 505-508. Below that, your application may be screened before a human reads it. A 512+ keeps every door open. But above the floor, it is one of many factors -- not a ranking algorithm.' },
  { heading: 'GPA separates applicants at the screening stage.', body: 'A cumulative GPA below 3.5 will close doors at top research programs regardless of everything else. But schools weight science GPA heavily -- a 3.6 cGPA with a 3.8 sGPA reads better than the reverse. Upward trends matter too.' },
  { heading: 'Both stats together determine your school list.', body: 'A 515 MCAT and a 3.6 GPA is a stronger position than a 510 and a 3.9. The combination, not either number alone, determines where you are competitive. PreVisr uses the worst-axis rule: your weaker stat anchors your category.' },
];

const DOESNT_DEFINE = [
  { heading: 'Holistic review is real.', body: 'Every MD school in the country is required to conduct holistic review. MCAT and GPA are the first filter -- after that, your research, clinical hours, personal statement, letters of rec, and interview performance all carry significant weight.' },
  { heading: 'A 505 is not a death sentence.', body: 'Students with a 505 match into residency every year. At the right school -- typically DO programs, regional MD schools, or mission-driven schools that prioritize underrepresented backgrounds -- a 505 with strong everything else can absolutely be competitive.' },
  { heading: 'The DO pathway is a real physician pathway.', body: 'DO and MD programs merged residency matches in 2020. A DO degree grants full prescriptive authority and access to every specialty. For students with stats in the 3.4-3.6 / 500-508 range, DO programs offer a legitimate path to the same career.' },
  { heading: 'Most successful applicants got rejected.', body: 'The average accepted applicant applied to 18 schools and was rejected from the majority of them. Getting in somewhere is the goal. A great doctor does not come from a specific zip code -- they come from showing up, growing, and not quitting.' },
];

const TAKEAWAYS = [
  { n: '512', label: 'MCAT to keep all MD doors open', sub: 'Below 505, expect screening at many programs' },
  { n: '3.7+', label: 'GPA for competitive MD applications', sub: 'Upward trend matters; science GPA weighted heavily' },
  { n: '18', label: 'Average schools applied', sub: 'Most accepted applicants were rejected by many schools' },
];

export default function Stats() {
  const isMobile = useIsMobile();
  const px = isMobile ? 20 : 60;
  const sectionPad = isMobile ? '56px 0' : '100px 0';
  const [session, setSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));
    return () => subscription.unsubscribe();
  }, []);

  return (
    <div style={{ background: WHITE, minHeight: '100vh', fontFamily: FONT }}>
      <PublicNav isMobile={isMobile} active="stats" />

      {/* Hero */}
      <div style={{ background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE_LIGHT_2} 100%)`, padding: isMobile ? `56px ${px}px 60px` : `100px ${px}px 90px`, textAlign: 'center' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: 'easeOut' }} style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: BLUE, background: WHITE, border: '1px solid ' + BLUE_LIGHT_2, borderRadius: 20, padding: '5px 14px', display: 'inline-block', marginBottom: 24 }}>
            National admissions data
          </div>
          <div style={{ fontSize: isMobile ? 32 : 48, fontWeight: 800, color: TEXT, letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: 20 }}>
            Medical school by the numbers.
          </div>
          <div style={{ fontSize: isMobile ? 15 : 18, color: TEXT_MUTED, lineHeight: 1.6, maxWidth: 600, margin: '0 auto' }}>
            The data behind medical school admissions, explained plainly. What the numbers mean, where you stand, and why they tell only part of the story.
          </div>
        </motion.div>
      </div>

      {/* Section 1: MD vs DO */}
      <div style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>The landscape</Eyebrow>
            <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              MD vs DO at a glance
            </div>
            <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 28, maxWidth: 620 }}>
              There are two paths to becoming a physician in the United States. Both produce fully licensed doctors. The numbers look different &mdash; here is why that matters and why it does not.
            </div>
          </FadeIn>
          <FadeIn isMobile={isMobile} delay={0.1}>
            <div style={{ border: '1px solid ' + BORDER, borderRadius: 14, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr 1fr' : '2fr 1fr 1fr', background: OFF_WHITE, padding: '12px 16px', borderBottom: '1px solid ' + BORDER }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: TEXT_FAINT }}>{isMobile ? '' : 'Metric'}</div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: BLUE, textAlign: 'center' }}>MD</div>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: SLATE, textAlign: 'center' }}>DO</div>
              </div>
              {MD_DO.map(function (row, i) {
                return (
                  <div key={row.metric} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr 1fr' : '2fr 1fr 1fr', padding: '14px 16px', borderBottom: i === MD_DO.length - 1 ? 'none' : '1px solid ' + BORDER, background: i % 2 === 0 ? WHITE : OFF_WHITE }}>
                    <div style={{ fontSize: isMobile ? 12 : 14, color: TEXT_MUTED, fontWeight: 500 }}>{row.metric}</div>
                    <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: BLUE, textAlign: 'center' }}>{row.md}</div>
                    <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: SLATE, textAlign: 'center' }}>{row.do}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: TEXT_FAINT }}>
              Source: AAMC 2024-2025 Applicant Data; AACOM 2024 Annual Report. Figures reflect the most recent completed application cycle.
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Section 2: Why they matter */}
      <div style={{ background: BLUE_LIGHT, padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>Why they matter</Eyebrow>
            <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              What MCAT and GPA actually do
            </div>
            <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 28, maxWidth: 620 }}>
              Numbers are not the whole story &mdash; but they are the first chapter. Here is what they concretely determine in your application.
            </div>
          </FadeIn>
          {MATTERS.map(function (item, i) {
            return (
              <FadeIn key={item.heading} isMobile={isMobile} delay={i * 0.1} style={{ marginBottom: 12 }}>
                <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: 12, padding: isMobile ? '18px 16px' : '20px 24px', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: BLUE, flexShrink: 0, marginTop: 6 }} />
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 6 }}>{item.heading}</div>
                    <div style={{ fontSize: 14, color: TEXT_MUTED, lineHeight: 1.7 }}>{item.body}</div>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>

      {/* Section 3: Why they don't define you */}
      <div style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>The fuller picture</Eyebrow>
            <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              Why the numbers do not define you
            </div>
            <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 28, maxWidth: 620 }}>
              Admissions committees read thousands of applications. The ones they remember are not the highest MCAT scores &mdash; they are the ones that made them believe someone would be a great doctor.
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 24 }}>
            {DOESNT_DEFINE.map(function (item, i) {
              return (
                <FadeIn key={item.heading} isMobile={isMobile} delay={i * 0.1}>
                  <div style={{ background: OFF_WHITE, border: '1px solid ' + BORDER, borderRadius: 12, padding: isMobile ? '18px 16px' : '20px 24px', height: '100%', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 8 }}>{item.heading}</div>
                    <div style={{ fontSize: 14, color: TEXT_MUTED, lineHeight: 1.7 }}>{item.body}</div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
          <FadeIn isMobile={isMobile} delay={0.2}>
            <div style={{ background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARKER} 100%)`, borderRadius: 14, padding: isMobile ? '22px 20px' : '28px 32px' }}>
              <div style={{ fontSize: isMobile ? 16 : 19, fontWeight: 700, color: WHITE, lineHeight: 1.4, marginBottom: 10 }}>
                &ldquo;Getting in somewhere is the goal. The best doctors in the country trained at schools you have never heard of.&rdquo;
              </div>
              <div style={{ fontSize: 12, color: BLUE_LIGHT_2, fontWeight: 500 }}>A reminder worth keeping</div>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Section 4: Demographics */}
      <div style={{ background: OFF_WHITE, padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>Who applies</Eyebrow>
            <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              Race and ethnicity of MD applicants
            </div>
            <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 28, maxWidth: 620 }}>
              The applicant pool is diverse &mdash; and medical schools are actively working to build classes that reflect the patients they will serve. Background and lived experience are part of the story admissions committees read.
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12 }}>
            {DEMOGRAPHICS.map(function (d, i) {
              return (
                <FadeIn key={d.group} isMobile={isMobile} delay={i * 0.05}>
                  <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: 10, padding: '16px 14px' }}>
                    <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 800, color: BLUE, letterSpacing: '-0.5px', marginBottom: 4 }}>{d.pct}</div>
                    <div style={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.4 }}>{d.group}</div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
          <div style={{ marginTop: 12, fontSize: 12, color: TEXT_FAINT }}>
            Source: AAMC 2024 Applicant and Matriculant Data. Percentages are of total applicants who self-reported race/ethnicity.
          </div>
        </div>
      </div>

      {/* Section 5: Key takeaways */}
      <div style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>Bottom line</Eyebrow>
            <div style={{ fontSize: isMobile ? 26 : 38, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 28 }}>
              What to take from this
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16 }}>
            {TAKEAWAYS.map(function (item, i) {
              return (
                <FadeIn key={item.n} isMobile={isMobile} delay={i * 0.1}>
                  <div style={{ background: OFF_WHITE, border: '1px solid ' + BORDER, borderRadius: 14, padding: isMobile ? '22px 18px' : '26px 22px', height: '100%', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: 36, fontWeight: 800, color: BLUE, letterSpacing: '-1px', marginBottom: 8 }}>{item.n}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: TEXT, marginBottom: 4 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: TEXT_FAINT, lineHeight: 1.5 }}>{item.sub}</div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARKER} 100%)`, padding: isMobile ? `52px ${px}px 60px` : `90px ${px}px`, textAlign: 'center' }}>
        <FadeIn isMobile={isMobile} style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ fontSize: isMobile ? 26 : 40, fontWeight: 800, color: WHITE, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 16 }}>
            See where your stats actually land.
          </div>
          <div style={{ fontSize: isMobile ? 14 : 16, color: BLUE_LIGHT_2, lineHeight: 1.6, marginBottom: 32 }}>
            PreVisr matches you to schools based on real historical data &mdash; in seconds.
          </div>
          <HoverBtn to={session ? '/matcher' : '/signup'} big full={isMobile}>Find my matches</HoverBtn>
        </FadeIn>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid ' + BORDER, padding: isMobile ? `24px ${px}px` : '28px 60px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isMobile ? 14 : 0 }}>
        <div style={{ fontSize: 13, color: TEXT_FAINT }}>PreVisr &mdash; Medical school matching for pre-meds</div>
        <div style={{ display: 'flex', gap: 24 }}>
          <Link to="/" style={{ fontSize: 12, color: TEXT_FAINT, textDecoration: 'none' }}>Home</Link>
          <Link to="/guide" style={{ fontSize: 12, color: TEXT_FAINT, textDecoration: 'none' }}>Guide</Link>
          <Link to="/about" style={{ fontSize: 12, color: TEXT_FAINT, textDecoration: 'none' }}>About</Link>
          <Link to="/privacy" style={{ fontSize: 12, color: TEXT_FAINT, textDecoration: 'none' }}>Privacy</Link>
          <Link to="/terms" style={{ fontSize: 12, color: TEXT_FAINT, textDecoration: 'none' }}>Terms</Link>
        </div>
      </div>

    </div>
  );
}
